<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcPaymentHeader',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'deleted']);

  const props = defineProps({
    ...userComponentProps,
    payment: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isNew = computed(() => {
    return !props.payment.id;
  });
  const isPettyCash = computed(() => {
    return props.payment.transaction_kind === 'cash_book';
  });
  const resourceKey = computed(() => {
    return isNew.value
      ? props.config.newTransaction
      : props.payment.snapshot('transaction_number');
  });

  watch(
    () => props.payment,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('payment');
    }
  );
  watch(
    () => props.config,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('config');
    }
  );

  const act_1040167445267876_emit_c2gcvjxhp_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_3083451129821185_emit_36v3sd4yg_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4892568850936438_emit_xzotsxbrs_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_1040167445267876_emit_c2gcvjxhp = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_c2gcvjxhp_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_1040167445267876_clickPreventStop_il6pyk0t7 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_c2gcvjxhp,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_1040167445267876_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_clickPreventStop_il6pyk0t7],
      event: 'click.prevent.stop',
    });
  const act_3083451129821185_emit_36v3sd4yg = new actionTypes.EmitAction({
    actionArgs: act_3083451129821185_emit_36v3sd4yg_getActionArgs,
  });
  const inst_evh_3083451129821185_success_7amegiyue =
    new actionTypes.EventHandlerImpl({
      action: act_3083451129821185_emit_36v3sd4yg,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_3083451129821185_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3083451129821185_success_7amegiyue],
    event: 'success',
  });
  const act_4892568850936438_emit_xzotsxbrs = new actionTypes.EmitAction({
    actionArgs: act_4892568850936438_emit_xzotsxbrs_getActionArgs,
  });
  const inst_evh_4892568850936438_success_e7fomovjt =
    new actionTypes.EventHandlerImpl({
      action: act_4892568850936438_emit_xzotsxbrs,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_4892568850936438_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4892568850936438_success_e7fomovjt],
    event: 'success',
  });

  const actions = {
    inst_evh_1040167445267876_clickPreventStop_il6pyk0t7,
    reload: inst_evh_1040167445267876_clickPreventStop_il6pyk0t7,
    inst_evh_1040167445267876_clickPreventStop,
    inst_evh_3083451129821185_success_7amegiyue,
    inst_evh_3083451129821185_success,
    inst_evh_4892568850936438_success_e7fomovjt,
    inst_evh_4892568850936438_success,
  };

  const evh_1040167445267876_clickPreventStop_il6pyk0t7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop_il6pyk0t7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3083451129821185_success_7amegiyue = async (event, scope) => {
    return await actions.inst_evh_3083451129821185_success_7amegiyue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3083451129821185_success = async (event, scope) => {
    return await actions.inst_evh_3083451129821185_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892568850936438_success_e7fomovjt = async (event, scope) => {
    return await actions.inst_evh_4892568850936438_success_e7fomovjt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4892568850936438_success = async (event, scope) => {
    return await actions.inst_evh_4892568850936438_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpBox>
        <ItpText muted class="mb-0">
          {{ config.resourceTitle }}
        </ItpText>
        <UcPaymentTransactionTypeSymbol
          :transactionType="payment.transaction_type"
          class="mx-2"
          v-if="payment.transaction_type"
        >
        </UcPaymentTransactionTypeSymbol>
      </ItpBox>
      <ItpText tag="h4" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <div class="btn-toolbar ms-auto flex-nowrap gap-2">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        v-if="!isNew"
        @click.prevent.stop="evh_1040167445267876_clickPreventStop($event, {})"
      >
      </ItpButton>
      <div class="ms-auto" v-if="!payment.isNew"></div>
      <BtnSyncResource
        :resource="payment"
        target="irent"
        confirmMessage="A tranzakciót csak egyszer lehet felölteni a régi rendszerbe,
feltöltés után a tranzakciót már nem lehet módosítani."
        v-if="!payment.isNew"
        @success="evh_3083451129821185_success($event, {})"
      >
      </BtnSyncResource>
      <ItpButton
        variant="light"
        icon="icon-park-outline:preview-open"
        :href="payment.meta.preview_url"
        :tooltip="pgettext('Payment', 'Open preview')"
        target="_blank"
        use-auth
        show-spinner
        v-if="payment.meta && payment.meta.preview_url"
      >
      </ItpButton>
      <BDropdown right variant="light" class="border" v-if="!isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="payment"
          :tooltip="pgettext('Payment', 'Delete payment')"
          :disabled="payment.isReadonly"
          v-if="!isNew"
          @success="evh_4892568850936438_success($event, {})"
        >
        </BtnDeleteResource>
      </BDropdown>
    </div>
  </ItpBox>
</template>
